// The light/dark/system choice both switches share (full phase 093): the
// cabinet's `theme` controller (`doc/base-ui-seo-pwa-i18n.md` 2.4) and the
// public footer's `appearance` controller.
//
// `<html data-theme>` is the page's scheme: `light`/`dark` pin it, no
// attribute leaves it to the browser (`color-scheme: light dark`). The choice
// is remembered in `localStorage` under one key, which the inline script in
// each layout's head reads before first paint. What is stored depends on the
// scheme the page starts from: a choice equal to it is forgotten, any other —
// `system` included, when the page's own default is not `system` — is kept.
// The cabinet starts from `system`, so it stores only `light` and `dark`, as
// before; a breeder Site starts from its owner's `Site#color_scheme`.
export const THEMES = ["system", "light", "dark"] as const
export type Theme = (typeof THEMES)[number]

export const STORAGE_KEY = "kotofon.theme"

export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value)
}

type ThemedRoot = Pick<Element, "getAttribute" | "setAttribute" | "removeAttribute">
type ThemeStorage = Pick<Storage, "setItem" | "removeItem">

// The scheme the page shows now: the attribute, or `system` without one.
export function currentTheme(root: Pick<Element, "getAttribute">): Theme {
  const rendered = root.getAttribute("data-theme")
  return rendered === "light" || rendered === "dark" ? rendered : "system"
}

export function applyTheme(root: ThemedRoot, theme: Theme): void {
  if (theme === "system") {
    root.removeAttribute("data-theme")
  } else {
    root.setAttribute("data-theme", theme)
  }
}

// What the browser keeps for `theme` over a page whose default is
// `defaultTheme`: `null` forgets the key.
export function storedTheme(theme: Theme, defaultTheme: Theme): Theme | null {
  return theme === defaultTheme ? null : theme
}

// `storage` is asked inside the guard: reading `window.localStorage` itself
// throws where site data is blocked.
export function rememberTheme(storage: () => ThemeStorage, key: string, theme: Theme, defaultTheme: Theme): void {
  const stored = storedTheme(theme, defaultTheme)
  try {
    if (stored === null) {
      storage().removeItem(key)
    } else {
      storage().setItem(key, stored)
    }
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the
    // page is themed either way and only the memory is lost.
  }
}

// `aria-pressed` of each option button, by the choice its `data-*-choice-param`
// names (`param` is that dataset key: `themeChoiceParam`, `appearanceChoiceParam`).
type ThemeOption = Pick<HTMLElement, "setAttribute"> & { dataset: DOMStringMap }

export function reflectTheme(options: Iterable<ThemeOption>, param: string, theme: Theme): void {
  for (const option of options) {
    option.setAttribute("aria-pressed", String(option.dataset[param] === theme))
  }
}
